import { EVENT_LABELS, type EventType } from '@siteday/shared';
import { z } from 'zod';

/**
 * Revision snapshots are `unknown` on the wire (EntryRevisionSchema.snapshot).
 * The office UI understands this subset of the DayEntryDocument; anything
 * else is shown as "snapshot" without a diff.
 */
export const SnapshotSchema = z
  .object({
    notes: z.string().nullable().optional(),
    weatherCondition: z.string().nullable().optional(),
    temperatureF: z.number().nullable().optional(),
    crewLines: z
      .array(
        z.object({
          id: z.string(),
          userId: z.string(),
          userName: z.string().optional(),
          hours: z.string(),
          deletedAt: z.string().nullable().optional(),
        }),
      )
      .optional(),
    equipmentLines: z
      .array(
        z.object({
          id: z.string(),
          equipmentId: z.string(),
          equipmentName: z.string().optional(),
          hours: z.string(),
          deletedAt: z.string().nullable().optional(),
        }),
      )
      .optional(),
    quantityLines: z
      .array(
        z.object({
          id: z.string(),
          jobCostCodeId: z.string(),
          code: z.string().optional(),
          quantity: z.string(),
          deletedAt: z.string().nullable().optional(),
        }),
      )
      .optional(),
    events: z
      .array(
        z.object({
          id: z.string(),
          type: z.string(),
          durationMinutes: z.number().nullable(),
          description: z.string().nullable(),
          deletedAt: z.string().nullable().optional(),
        }),
      )
      .optional(),
    photos: z
      .array(z.object({ id: z.string(), deletedAt: z.string().nullable().optional() }))
      .optional(),
  })
  .passthrough();
export type Snapshot = z.infer<typeof SnapshotSchema>;

export function parseSnapshot(value: unknown): Snapshot | null {
  const r = SnapshotSchema.safeParse(value);
  return r.success ? r.data : null;
}

export interface DiffLine {
  section: 'Crew' | 'Equipment' | 'Quantities' | 'Delays' | 'Notes' | 'Weather' | 'Photos';
  label: string;
  before: string | null;
  after: string | null;
}

const live = <T extends { deletedAt?: string | null | undefined }>(xs: T[] | undefined): T[] =>
  (xs ?? []).filter((x) => !x.deletedAt);

function eventLabel(type: string): string {
  return (EVENT_LABELS as Record<string, string>)[type as EventType] ?? type;
}

/** Human-readable differences between two snapshots (what the revision changed). */
export function diffSnapshots(prev: Snapshot | null, next: Snapshot): DiffLine[] {
  const out: DiffLine[] = [];
  const p = prev ?? {};

  const byKey = <T>(xs: T[], key: (x: T) => string) => new Map(xs.map((x) => [key(x), x]));

  const pc = byKey(live(p.crewLines), (l) => l.userId);
  const nc = byKey(live(next.crewLines), (l) => l.userId);
  for (const [k, l] of nc) {
    const before = pc.get(k);
    const name = l.userName ?? before?.userName ?? 'Crew member';
    if (!before) out.push({ section: 'Crew', label: name, before: null, after: `${l.hours} h` });
    else if (before.hours !== l.hours)
      out.push({
        section: 'Crew',
        label: name,
        before: `${before.hours} h`,
        after: `${l.hours} h`,
      });
  }
  for (const [k, l] of pc)
    if (!nc.has(k))
      out.push({
        section: 'Crew',
        label: l.userName ?? 'Crew member',
        before: `${l.hours} h`,
        after: null,
      });

  const pe = byKey(live(p.equipmentLines), (l) => l.equipmentId);
  const ne = byKey(live(next.equipmentLines), (l) => l.equipmentId);
  for (const [k, l] of ne) {
    const before = pe.get(k);
    const name = l.equipmentName ?? before?.equipmentName ?? 'Machine';
    if (!before)
      out.push({ section: 'Equipment', label: name, before: null, after: `${l.hours} h` });
    else if (before.hours !== l.hours)
      out.push({
        section: 'Equipment',
        label: name,
        before: `${before.hours} h`,
        after: `${l.hours} h`,
      });
  }
  for (const [k, l] of pe)
    if (!ne.has(k))
      out.push({
        section: 'Equipment',
        label: l.equipmentName ?? 'Machine',
        before: `${l.hours} h`,
        after: null,
      });

  const pq = byKey(live(p.quantityLines), (l) => l.jobCostCodeId);
  const nq = byKey(live(next.quantityLines), (l) => l.jobCostCodeId);
  for (const [k, l] of nq) {
    const before = pq.get(k);
    const name = l.code ?? before?.code ?? 'Cost code';
    if (!before) {
      if (Number(l.quantity) !== 0)
        out.push({ section: 'Quantities', label: name, before: null, after: l.quantity });
    } else if (Number(before.quantity) !== Number(l.quantity)) {
      out.push({ section: 'Quantities', label: name, before: before.quantity, after: l.quantity });
    }
  }
  for (const [k, l] of pq)
    if (!nq.has(k))
      out.push({
        section: 'Quantities',
        label: l.code ?? 'Cost code',
        before: l.quantity,
        after: null,
      });

  const pv = byKey(live(p.events), (e) => e.id);
  const nv = byKey(live(next.events), (e) => e.id);
  const evText = (e: { durationMinutes: number | null; description: string | null }) =>
    [e.durationMinutes !== null ? `${e.durationMinutes} min` : null, e.description]
      .filter(Boolean)
      .join(' — ') || '—';
  for (const [k, e] of nv) {
    const before = pv.get(k);
    if (!before)
      out.push({ section: 'Delays', label: eventLabel(e.type), before: null, after: evText(e) });
    else if (evText(before) !== evText(e))
      out.push({
        section: 'Delays',
        label: eventLabel(e.type),
        before: evText(before),
        after: evText(e),
      });
  }
  for (const [k, e] of pv)
    if (!nv.has(k))
      out.push({ section: 'Delays', label: eventLabel(e.type), before: evText(e), after: null });

  if ((p.notes ?? null) !== (next.notes ?? null) && prev !== null) {
    out.push({
      section: 'Notes',
      label: 'Notes',
      before: p.notes ?? '—',
      after: next.notes ?? '—',
    });
  }
  if (
    prev !== null &&
    ((p.weatherCondition ?? null) !== (next.weatherCondition ?? null) ||
      (p.temperatureF ?? null) !== (next.temperatureF ?? null))
  ) {
    const w = (s: Snapshot) =>
      [
        s.weatherCondition,
        s.temperatureF !== null && s.temperatureF !== undefined ? `${s.temperatureF}°F` : null,
      ]
        .filter(Boolean)
        .join(' · ') || '—';
    out.push({ section: 'Weather', label: 'Weather', before: w(p), after: w(next) });
  }
  const pp = live(p.photos).length;
  const np = live(next.photos).length;
  if (prev !== null && pp !== np)
    out.push({ section: 'Photos', label: 'Photos', before: String(pp), after: String(np) });

  return out;
}
